class SiteHeader {
  constructor (root) {
    this.root = root
    this.menu = root.querySelector('[data-mobile-menu]')
    this.menuToggle = root.querySelector('[data-mobile-menu-toggle]')
    this.menuClose = root.querySelector('[data-mobile-menu-close]')
    this.menuOverlay = root.querySelector('[data-mobile-menu-overlay]')
    this.searchPanel = root.querySelector('[data-header-search]')
    this.searchToggle = root.querySelector('[data-header-search-toggle]')
    this.searchClose = root.querySelector('[data-header-search-close]')
    this.searchInput = root.querySelector('[data-header-search-input]')
    this.megaMenuItems = Array.from(root.querySelectorAll('[data-mega-menu-item]'))
    this.legacyMobileMenu = document.getElementById('menu')
    this.legacyMobileMenuToggles = Array.from(document.querySelectorAll('[mobile-nav-toggle]'))
    this.legacyMobileMenuPages = Array.from(this.legacyMobileMenu?.querySelectorAll('.mobile-menu-page') || [])
    this.activeMegaMenuItem = null
    this.megaMenuCloseTimeout = null
    this.lastFocusedElement = null

    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)

    this.bindEvents()
    this.initMegaMenus()
    this.handleScroll()
  }

  bindEvents () {
    this.menuToggle?.addEventListener('click', () => this.openMenu())
    this.menuClose?.addEventListener('click', () => this.closeMenu())
    this.menuOverlay?.addEventListener('click', () => this.closeMenu())
    this.searchToggle?.addEventListener('click', () => this.toggleSearch())
    this.searchClose?.addEventListener('click', () => this.closeSearch())
    this.legacyMobileMenuToggles.forEach(toggle => {
      toggle.addEventListener('click', () => this.toggleLegacyMobileMenu())
    })
    this.legacyMobileMenu?.querySelectorAll('a[data-target-page], button[data-target-page]').forEach(control => {
      control.addEventListener('click', event => {
        const targetPage = event.currentTarget.dataset.targetPage
        if (!targetPage) return

        event.preventDefault()
        this.switchLegacyMobileMenuPage(targetPage)
      })
    })
    this.searchPanel?.addEventListener('submit', event => {
      if (!this.searchInput?.value.trim()) {
        event.preventDefault()
        this.openSearch()
      }
    })

    document.addEventListener('keydown', this.handleDocumentKeydown)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize)

    this.root.addEventListener('shopify:section:unload', () => this.destroy(), { once: true })
  }

  initMegaMenus () {
    this.megaMenuItems.forEach(item => {
      const menu = item.querySelector('[data-mega-menu]')
      if (!menu) return

      item.addEventListener('mouseenter', () => this.openMegaMenu(item))
      item.addEventListener('mouseleave', () => this.scheduleMegaMenuClose(item))
      item.addEventListener('focusin', () => this.openMegaMenu(item))
      item.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!item.contains(document.activeElement)) this.closeMegaMenu(item)
        }, 0)
      })
    })
  }

  openMegaMenu (item) {
    window.clearTimeout(this.megaMenuCloseTimeout)

    if (this.activeMegaMenuItem && this.activeMegaMenuItem !== item) {
      this.closeMegaMenu(this.activeMegaMenuItem)
    }

    const menu = item.querySelector('[data-mega-menu]')
    const trigger = item.querySelector('[data-mega-menu-trigger]')
    if (!menu || !trigger) return

    this.positionMegaMenu()
    menu.classList.add('is-open')
    trigger.setAttribute('aria-expanded', 'true')
    this.activeMegaMenuItem = item
  }

  scheduleMegaMenuClose (item) {
    window.clearTimeout(this.megaMenuCloseTimeout)
    this.megaMenuCloseTimeout = window.setTimeout(() => this.closeMegaMenu(item), 150)
  }

  closeMegaMenu (item = this.activeMegaMenuItem) {
    if (!item) return

    const menu = item.querySelector('[data-mega-menu]')
    const trigger = item.querySelector('[data-mega-menu-trigger]')
    menu?.classList.remove('is-open')
    trigger?.setAttribute('aria-expanded', 'false')

    if (this.activeMegaMenuItem === item) this.activeMegaMenuItem = null
  }

  positionMegaMenu () {
    const header = this.root.querySelector('.site-header')
    if (!header) return

    const headerBottom = Math.max(0, header.getBoundingClientRect().bottom)
    this.root.style.setProperty('--site-header-bottom', `${headerBottom}px`)
  }

  toggleLegacyMobileMenu () {
    if (!this.legacyMobileMenu) return

    if (document.body.classList.contains('open')) {
      this.closeLegacyMobileMenu()
    } else {
      this.openLegacyMobileMenu()
    }
  }

  openLegacyMobileMenu () {
    if (!this.legacyMobileMenu) return

    this.closeSearch()
    this.closeMegaMenu()
    this.lastFocusedElement = document.activeElement
    document.body.classList.add('open')
    this.legacyMobileMenu.setAttribute('aria-hidden', 'false')
    this.legacyMobileMenuToggles.forEach(toggle => toggle.setAttribute('aria-expanded', 'true'))
    window.setTimeout(() => this.legacyMobileMenu.querySelector('[mobile-nav-toggle]')?.focus(), 220)
  }

  closeLegacyMobileMenu () {
    if (!this.legacyMobileMenu || !document.body.classList.contains('open')) return

    document.body.classList.remove('open')
    this.legacyMobileMenu.setAttribute('aria-hidden', 'true')
    this.legacyMobileMenuToggles.forEach(toggle => toggle.setAttribute('aria-expanded', 'false'))
    this.switchLegacyMobileMenuPage('root')

    if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus()
  }

  switchLegacyMobileMenuPage (targetPage) {
    this.legacyMobileMenuPages.forEach(page => {
      page.classList.toggle('active', page.dataset.page === targetPage)
    })
  }

  openMenu () {
    if (!this.menu || !this.menuToggle || !this.menuOverlay) return

    this.closeSearch()
    this.lastFocusedElement = document.activeElement
    this.menu.classList.add('is-open')
    this.menuOverlay.hidden = false
    requestAnimationFrame(() => {
      this.menuOverlay.classList.add('is-open')
    })
    window.setTimeout(() => this.menuClose?.focus({ preventScroll: true }), 220)
    this.menu.setAttribute('aria-hidden', 'false')
    this.menuToggle.setAttribute('aria-expanded', 'true')
    document.body.classList.add('mobile-menu-open')
  }

  closeMenu () {
    if (!this.menu || !this.menuToggle || !this.menuOverlay) return

    this.menu.classList.remove('is-open')
    this.menuOverlay.classList.remove('is-open')
    this.menu.setAttribute('aria-hidden', 'true')
    this.menuToggle.setAttribute('aria-expanded', 'false')
    document.body.classList.remove('mobile-menu-open')

    window.setTimeout(() => {
      this.menuOverlay.hidden = true
    }, 220)

    if (this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus()
    }
  }

  toggleSearch () {
    if (!this.searchPanel || !this.searchToggle) return

    if (this.searchPanel.classList.contains('is-open')) {
      this.closeSearch()
    } else {
      this.openSearch()
    }
  }

  openSearch () {
    if (!this.searchPanel || !this.searchToggle) return

    this.searchPanel.classList.add('is-open')
    this.searchToggle.setAttribute('aria-expanded', 'true')
    this.searchInput?.setAttribute('aria-hidden', 'false')
    if (this.searchInput) this.searchInput.tabIndex = 0
    if (this.searchClose) this.searchClose.tabIndex = 0
    window.setTimeout(() => this.searchInput?.focus(), 0)
  }

  closeSearch () {
    if (!this.searchPanel || !this.searchToggle || !this.searchPanel.classList.contains('is-open')) return

    this.searchPanel.closest('predictive-search')?.close()
    this.searchPanel.classList.remove('is-open')
    this.searchToggle.setAttribute('aria-expanded', 'false')
    this.searchInput?.setAttribute('aria-hidden', 'true')
    if (this.searchInput) this.searchInput.tabIndex = -1
    if (this.searchClose) this.searchClose.tabIndex = -1
  }

  handleDocumentKeydown (event) {
    if (event.key === 'Escape') {
      if (document.body.classList.contains('open') && this.legacyMobileMenu) {
        this.closeLegacyMobileMenu()
        return
      }

      if (this.activeMegaMenuItem) {
        const trigger = this.activeMegaMenuItem.querySelector('[data-mega-menu-trigger]')
        this.closeMegaMenu()
        trigger?.focus()
        return
      }

      if (this.menu?.classList.contains('is-open')) {
        this.closeMenu()
        return
      }

      if (this.searchPanel?.classList.contains('is-open')) {
        this.closeSearch()
        this.searchToggle?.focus()
      }
    }

    if (event.key === 'Tab' && this.menu?.classList.contains('is-open')) {
      this.trapMenuFocus(event)
    }

    if (event.key === 'Tab' && document.body.classList.contains('open') && this.legacyMobileMenu) {
      this.trapLegacyMobileMenuFocus(event)
    }
  }

  trapLegacyMobileMenuFocus (event) {
    const focusableElements = Array.from(
      this.legacyMobileMenu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')
    ).filter(element => element.offsetParent !== null)

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  trapMenuFocus (event) {
    const focusableElements = Array.from(
      this.menu.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled])')
    ).filter(element => element.offsetParent !== null)

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  handleScroll () {
    this.root.classList.toggle('is-scrolled', window.scrollY > 24)
    if (this.activeMegaMenuItem) this.positionMegaMenu()
  }

  handleResize () {
    if (window.matchMedia('(min-width: 1200px)').matches) {
      this.closeMenu()
      this.closeLegacyMobileMenu()
      if (this.activeMegaMenuItem) this.positionMegaMenu()
    } else {
      this.closeMegaMenu()
    }

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.closeSearch()
    }
  }

  destroy () {
    document.removeEventListener('keydown', this.handleDocumentKeydown)
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }
}

class WishlistCounter {
  constructor (storageKey) {
    this.storageKey = storageKey
    this.counters = Array.from(document.querySelectorAll('[id^="wishlist-counter-"]'))
    if (this.counters.length === 0) return

    this.update = this.update.bind(this)
    window.addEventListener('storage', this.update)
    document.addEventListener('wishlist-updated', this.update)
    this.update()
  }

  update () {
    let count = 0

    try {
      const wishlist = JSON.parse(localStorage.getItem(this.storageKey) || '[]')
      count = Array.isArray(wishlist) ? wishlist.length : 0
    } catch (error) {
      count = 0
    }

    this.counters.forEach(counter => {
      counter.textContent = count > 0 ? String(count) : ''
      counter.classList.toggle('hidden', count === 0)
    })
  }
}

const initializeHeaders = (scope = document) => {
  scope.querySelectorAll('[data-site-header]').forEach(header => {
    if (header.dataset.initialized === 'true') return
    header.dataset.initialized = 'true'
    new SiteHeader(header)
  })
}

document.addEventListener('DOMContentLoaded', () => {
  initializeHeaders()
  window.wishlistCounter = new WishlistCounter('st-wishlist-local')

  document.querySelectorAll('.js-go-cart-counter').forEach(counter => {
    const updateVisibility = () => {
      const count = Number.parseInt(counter.textContent, 10) || 0
      counter.classList.toggle('hidden', count === 0)
    }

    updateVisibility()
    new MutationObserver(updateVisibility).observe(counter, {
      childList: true,
      characterData: true,
      subtree: true
    })
  })
})

document.addEventListener('shopify:section:load', event => {
  initializeHeaders(event.target)
})

// Language switcher: each language is a submit button, so choosing one applies
// it immediately. Here the panel only closes on an outside click or Escape.
document.querySelectorAll('[data-language-switcher] details').forEach((details) => {
  document.addEventListener('click', (event) => {
    if (details.open && !details.contains(event.target)) details.open = false
  })
  details.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && details.open) {
      details.open = false
      details.querySelector('summary')?.focus()
    }
  })
})
